import {
  ColumnData,
  SlideAction,
  type SlidePanelRegistrar,
  useSlidePanel,
} from '@gripp/shared-ui';
import { startTransition, useMemo } from 'react';
import {
  useLocation,
  useNavigate,
  useOutlet,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import { RoutineSubmitSource } from '../assetUtils';
import { AssetCard } from './assetCard';
import { AssetFilters } from './assetFilters';

const ASSET_DETAIL_PANEL_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/components/assetDetailPanel.tsx';
const ASSET_CARD_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/components/assetCard.tsx';
const ASSET_ROUTES_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/routes.tsx';
const ASSET_DETAIL_ROUTE_PATTERN = '/assets/:assetId';
const ASSET_DETAIL_ROUTE_PANEL_KEY = 'asset-detail';
const ASSET_CARD_PANEL_DEBUG_ID = 'asset-card';

type AssetDetailLocationState = {
  from?: string;
};

const getLocationStateSource = (state: unknown) =>
  state && typeof state === 'object' && !Array.isArray(state)
    ? (state as AssetDetailLocationState).from
    : undefined;

const AssetDetailRightPanel = ({ column }: { column: ColumnData | null }) => {
  useSlidePanel('right', column, [column]);
  return null;
};

export const AssetDetailPanel = () => {
  const { assetId, routineId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const outletContext = useOutletContext<SlidePanelRegistrar | null>();
  const outlet = useOutlet(outletContext);
  const hasChildPanel = !!outlet;
  const source =
    new URLSearchParams(location.search).get('from') ??
    getLocationStateSource(location.state);
  const routineSubmitOwnsRightPanel =
    !!routineId && source === RoutineSubmitSource.RoutinesList;

  const column = useMemo<ColumnData | null>(() => {
    if (!assetId) return null;
    return {
      title: 'assets.menu.title',
      mainComponent: <AssetCard assetId={assetId} />,
      headerComponent: <AssetFilters assetId={assetId} />,
      showBackButton: true,
      ...(hasChildPanel
        ? {
            onBackButtonPress: () => startTransition(() => navigate('.')),
          }
        : {
            backButtonTransition: SlideAction.SINGLE,
            onBackButtonPress: () => startTransition(() => navigate('..')),
          }),
      debug: {
        id: ASSET_CARD_PANEL_DEBUG_ID,
        titleKey: 'assets.menu.title',
        component: 'AssetCard',
        componentSource: ASSET_CARD_SOURCE,
        columnSource: ASSET_DETAIL_PANEL_SOURCE,
        ownerComponent: 'AssetDetailPanel',
        ownerSource: ASSET_DETAIL_PANEL_SOURCE,
        routePanelKey: ASSET_DETAIL_ROUTE_PANEL_KEY,
        routePattern: ASSET_DETAIL_ROUTE_PATTERN,
        routeSource: ASSET_ROUTES_SOURCE,
        source: {
          owner: {
            file: ASSET_DETAIL_PANEL_SOURCE,
            line: 17,
            symbol: 'AssetDetailPanel',
          },
          column: {
            file: ASSET_DETAIL_PANEL_SOURCE,
            line: 21,
            symbol: 'column',
          },
          component: {
            file: ASSET_CARD_SOURCE,
            line: 30,
            symbol: 'AssetCard',
          },
          route: {
            file: ASSET_ROUTES_SOURCE,
            line: 67,
            symbol: 'assetsRoutes',
          },
        },
        entity: { type: 'asset', id: assetId },
      },
    };
  }, [assetId, hasChildPanel, navigate]);

  return (
    <>
      {!routineSubmitOwnsRightPanel && (
        <AssetDetailRightPanel column={column} />
      )}
      {outlet}
    </>
  );
};
